import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import ProductForm from "../components/ProductForm";
import ProductPreview from "../components/ProductPreview";
import { 
  Package, 
  Search, 
  Filter,
  RefreshCw,
  ArrowLeft,
  Plus,
  Edit,
  Eye,
  Trash2,
  AlertCircle,
  CheckCircle,
  Star,
  DollarSign
} from "lucide-react";

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [featuredFilter, setFeaturedFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // New status filter
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 20;
  const navigate = useNavigate();

  // New state for product management
  const [showProductForm, setShowProductForm] = useState(false);
  const [showProductPreview, setShowProductPreview] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return null;
    }
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  // Product CRUD operations
  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowProductForm(true);
  };

  const handleEditProduct = async (product) => {
    try {
      // Fetch full product details for editing
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${product._id}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product details');
      }

      const productDetails = await response.json();
      setEditingProduct(productDetails);
      setShowProductForm(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewProduct = async (product) => {
    try {
      // Fetch full product details for preview
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${product._id}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product details');
      }

      const productDetails = await response.json();
      setPreviewProduct(productDetails);
      setShowProductPreview(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteProduct = (product) => {
    setDeleteConfirm(product);
  };

  const handleToggleProductStatus = async (product) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${product._id}/toggle-status`,
        {
          method: 'PATCH',
          headers
        }
      );

      if (!response.ok) {
        throw new Error('Failed to toggle product status');
      }

      const result = await response.json();
      
      // Update local product list
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p._id === product._id 
            ? { ...p, status: result.status }
            : p
        )
      );

      console.log(`✅ Product ${product.name} status changed to: ${result.status}`);
      
    } catch (err) {
      console.error('Error toggling product status:', err);
      setError(err.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${deleteConfirm._id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      setError("");
      setDeleteConfirm(null);
      fetchProducts(); // Refresh the list
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const url = editingProduct 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${editingProduct._id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingProduct ? 'update' : 'create'} product`);
      }

      setShowProductForm(false);
      setEditingProduct(null);
      setError("");
      fetchProducts(); // Refresh the list
    } catch (err) {
      throw new Error(err.message);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        limit: productsPerPage.toString(),
        offset: ((currentPage - 1) * productsPerPage).toString()
      });

      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (featuredFilter && featuredFilter !== 'all') params.append('featured', featuredFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const productsData = await response.json();
      setProducts(productsData);

      // Get total count for pagination
      const countParams = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'all') countParams.append('category', categoryFilter);
      if (searchTerm) countParams.append('search', searchTerm);
      if (featuredFilter && featuredFilter !== 'all') countParams.append('featured', featuredFilter);
      if (statusFilter && statusFilter !== 'all') countParams.append('status', statusFilter);

      const countResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/count?${countParams}`);
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalProducts(countData.count);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
      if (response.ok) {
        const categoriesData = await response.json();
        setCategories(categoriesData);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  // Removed WooCommerce sync functionality - replaced with direct product management

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [currentPage, categoryFilter, searchTerm, featuredFilter]);

  const formatPrice = (price) => {
    const numPrice = parseFloat(price || 0);
    return numPrice > 0 ? `$${numPrice.toFixed(2)}` : 'Price on request';
  };

  const totalPages = Math.ceil(totalProducts / productsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button asChild variant="ghost" size="sm">
                <div onClick={() => navigate('/admin/dashboard')} className="cursor-pointer flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Dashboard
                </div>
              </Button>
              <div className="flex items-center gap-3">
                <div className="bg-purple-600 w-10 h-10 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Manage Products</h1>
                  <p className="text-sm text-gray-500">{totalProducts} total products</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button onClick={handleAddProduct} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add New Product
              </Button>
              
              <Button onClick={fetchProducts} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search products by name, SKU, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category._id} value={category.slug || category.name}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={featuredFilter} onValueChange={setFeaturedFilter}>
                <SelectTrigger className="w-40">
                  <Star className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Featured" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="true">Featured Only</SelectItem>
                  <SelectItem value="false">Not Featured</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm("");
                  setCategoryFilter("all");
                  setFeaturedFilter("all");
                  setStatusFilter("all");
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Products Grid */}
        <Card>
          <CardHeader>
            <CardTitle>Products</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-2 text-gray-600">Loading products...</span>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || categoryFilter ? "Try adjusting your filters" : "Add new products using the 'Add New Product' button above"}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
                  {products.map((product) => {
                    const primaryImage = product.images && product.images.length > 0 
                      ? product.images[0].src 
                      : "https://placehold.co/300x300/e5e7eb/6b7280?text=No+Image";
                    
                    const price = parseFloat(product.price || product.regular_price || 0);

                    return (
                      <Card key={product.id} className="group hover:shadow-lg transition-shadow">
                        <div className="aspect-square relative overflow-hidden bg-gray-100 rounded-t-lg">
                          <img 
                            src={primaryImage}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = "https://placehold.co/300x300/e5e7eb/6b7280?text=No+Image";
                            }}
                          />
                          
                          {/* Active/Inactive Toggle */}
                          <div className="absolute top-3 right-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleProductStatus(product);
                              }}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                product.status === 'publish'
                                  ? 'bg-green-500 text-white hover:bg-green-600'
                                  : 'bg-gray-400 text-white hover:bg-gray-500'
                              }`}
                              title={product.status === 'publish' ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                            >
                              <div className={`w-2 h-2 rounded-full ${product.status === 'publish' ? 'bg-white' : 'bg-gray-200'}`}></div>
                              {product.status === 'publish' ? 'ACTIVE' : 'INACTIVE'}
                            </button>
                          </div>

                          {/* Badges */}
                          <div className="absolute top-3 left-3 flex flex-col gap-2">
                            {product.featured && (
                              <Badge className="bg-yellow-600 hover:bg-yellow-700 text-white">
                                <Star className="w-3 h-3 mr-1" />
                                FEATURED
                              </Badge>
                            )}
                            {product.stock_status === 'outofstock' && (
                              <Badge className="bg-red-600 text-white">
                                OUT OF STOCK
                              </Badge>
                            )}
                          </div>

                          {/* Quick Actions */}
                          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="secondary" 
                                className="bg-white/90 hover:bg-white"
                                onClick={() => handleViewProduct(product)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="secondary" 
                                className="bg-white/90 hover:bg-white"
                                onClick={() => handleEditProduct(product)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                className="bg-red-500/90 hover:bg-red-500"
                                onClick={() => handleDeleteProduct(product)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        <CardContent className="p-4 space-y-3">
                          <div>
                            <h3 className="font-semibold text-gray-900 line-clamp-2 text-sm">
                              {product.name}
                            </h3>
                            
                            {product.sku && (
                              <p className="text-xs text-gray-500 font-mono mt-1">
                                SKU: {product.sku}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-purple-600" />
                              <span className="text-lg font-bold text-purple-600">
                                {formatPrice(price)}
                              </span>
                            </div>
                            
                            {product.stock_quantity && (
                              <span className="text-sm text-gray-500">
                                {product.stock_quantity} in stock
                              </span>
                            )}
                          </div>

                          {product.categories && product.categories.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {product.categories.slice(0, 2).map((category) => (
                                <Badge 
                                  key={category.id} 
                                  variant="outline" 
                                  className="text-xs text-purple-600 border-purple-200"
                                >
                                  {category.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-700">
                      Showing {((currentPage - 1) * productsPerPage) + 1} to {Math.min(currentPage * productsPerPage, totalProducts)} of {totalProducts} products
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      
                      {(() => {
                        // Calculate page range to display
                        const maxPagesToShow = 5;
                        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                        
                        // Adjust if we're at the end
                        if (endPage - startPage < maxPagesToShow - 1) {
                          startPage = Math.max(1, endPage - maxPagesToShow + 1);
                        }
                        
                        // Generate array of unique page numbers
                        const pages = [];
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(i);
                        }
                        
                        return pages.map(pageNum => (
                          <Button
                            key={pageNum}
                            variant={pageNum === currentPage ? "default" : "outline"}
                            onClick={() => setCurrentPage(pageNum)}
                            className={pageNum === currentPage ? "bg-purple-600" : ""}
                          >
                            {pageNum}
                          </Button>
                        ));
                      })()}
                      
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductForm
          product={editingProduct}
          categories={categories}
          onSave={handleSaveProduct}
          onCancel={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
          isEditing={!!editingProduct}
        />
      )}

      {/* Product Preview Modal */}
      {showProductPreview && previewProduct && (
        <ProductPreview
          product={previewProduct}
          onClose={() => {
            setShowProductPreview(false);
            setPreviewProduct(null);
          }}
          onEdit={() => {
            setShowProductPreview(false);
            setEditingProduct(previewProduct);
            setShowProductForm(true);
            setPreviewProduct(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-2 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Product</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "<strong>{deleteConfirm.name}</strong>"?
            </p>
            
            <div className="flex items-center justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={confirmDelete}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Product
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;